import React, { useEffect, useMemo, useRef, useState } from 'react';
import { readWorkbook, sheetToJson } from '../utils/excelExport';

export default function XlsxPreview({ url, thumbUrl, fallbackUrl, className = '' }) {
  const workbookRef = useRef(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [data, setData] = useState({ rows: [], cols: 0, sheetName: '' });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const MAX_ROWS = 200; // cap to keep preview snappy
  const MAX_COLS = 20;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData({ rows: [], cols: 0, sheetName: '' });
    setSheetNames([]);
    setSelectedSheet('');
    workbookRef.current = null;

    (async () => {
      try {
        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) throw new Error(await resp.text());
        const buf = await resp.arrayBuffer();
        if (cancelled) return;
        const wb = await readWorkbook(buf);
        if (cancelled) return;
        workbookRef.current = wb;
        const names = wb.worksheets.map((ws) => ws.name);
        setSheetNames(names);
        setSelectedSheet(names[0] || '');
      } catch (e) {
        if (!cancelled) setError(e.message || 'Kunde inte läsa XLSX');
      }
    })();

    return () => { cancelled = true; };
  }, [url]);

  useEffect(() => {
    if (!workbookRef.current || !selectedSheet) {
      setData({ rows: [], cols: 0, sheetName: selectedSheet || '' });
      setLoading(false);
      return;
    }

    const ws = workbookRef.current.getWorksheet(selectedSheet);
    if (!ws) {
      setData({ rows: [], cols: 0, sheetName: selectedSheet });
      setLoading(false);
      return;
    }

    const aoa = sheetToJson(ws, { firstRowAsHeader: false });
    const rows = aoa.slice(0, MAX_ROWS).map(r => r.slice(0, MAX_COLS));
    const cols = Math.min(MAX_COLS, Math.max(0, ...(rows.map(r => r.length))));
    setData({ rows, cols, sheetName: selectedSheet });
    setLoading(false);
  }, [selectedSheet]);

  const hasMore = useMemo(() => error == null && data.rows.length === MAX_ROWS, [data, error]);
  const hasMultipleSheets = sheetNames.length > 1;

  return (
    <div className={`relative w-full h-full overflow-auto bg-white dark:bg-slate-900 ${className}`}>
      {loading && <div className="p-4 text-center text-xs text-slate-500">Laddar…</div>}
      {error && (
        <div className="p-4 text-center text-xs text-red-600 dark:text-red-400">
          <div>{error}</div>
          {fallbackUrl && (
            <div className="mt-2 flex flex-col items-center gap-2">
              <a
                href={fallbackUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100"
              >
                Öppna i nytt fönster
              </a>
              <span className="text-[11px] text-slate-500 dark:text-slate-300">Förhandsvisning blockerades (CORS eller ogiltigt XLSX).</span>
            </div>
          )}
          {thumbUrl && (
            <div className="mt-3 flex flex-col items-center gap-2 text-slate-500 dark:text-slate-300">
              <img src={thumbUrl} alt="Tumnagel" className="max-h-64 w-full rounded border border-slate-200 object-contain dark:border-slate-700" />
              <span className="text-[11px]">Visar tumnagel – tabellförhandsvisning misslyckades.</span>
            </div>
          )}
        </div>
      )}
      {!loading && !error && (
        <div className="p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300">
            <span>Blad:</span>
            {sheetNames.length ? (
              <label className="flex items-center gap-1">
                <span className="sr-only">Välj kalkylblad</span>
                <select
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                  value={selectedSheet}
                  onChange={e => setSelectedSheet(e.target.value)}
                >
                  {sheetNames.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </label>
            ) : (
              <span>—</span>
            )}
            {hasMultipleSheets && <span className="text-slate-400">({sheetNames.length} blad)</span>}
          </div>
          <div className="overflow-auto border rounded border-slate-200 dark:border-slate-700">
            <table className="min-w-full text-[11px]">
              <tbody>
                {data.rows.map((row, rIdx) => (
                  <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800'}>
                    {Array.from({ length: data.cols }).map((_, cIdx) => (
                      <td key={cIdx} className="px-2 py-1 border-t border-slate-200 dark:border-slate-700 whitespace-nowrap">
                        {row[cIdx] == null ? '' : String(row[cIdx])}
                      </td>)
                    )}
                  </tr>
                ))}
                {data.rows.length === 0 && (
                  <tr><td className="p-6 text-center text-slate-500">Tomt blad</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="mt-2 text-[11px] text-slate-500">Visar endast de första {MAX_ROWS} raderna och {MAX_COLS} kolumnerna.</div>
          )}
        </div>
      )}
    </div>
  );
}
